// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Alert, Typography } from '@mui/material';

// api
import type { TimestampsSummaryModel } from '@/api/generated/energyAnalysis/endpoints/energyData';

// relative
import type { TimeRangeMillis } from './timestampsSummaryEvaluation';

import { DateTime } from 'luxon';

type TimestampSummaryLike = {
  maxEarliestDataTimestamp?: string | DateTime | null;
  minLatestDataTimestamp?: string | DateTime | null;
} & Partial<TimestampsSummaryModel>;

type TimestampSummaryIndicatorProps = {
  globalTimeRange?: Partial<TimeRangeMillis> | null;
  timestampSummary?: TimestampSummaryLike | null;
};

function toDateTime(
  value: string | DateTime | null | undefined,
): DateTime | null {
  if (!value) {
    return null;
  }

  if (DateTime.isDateTime(value)) {
    return value.isValid ? value : null;
  }

  const parsed = DateTime.fromISO(value);
  return parsed.isValid ? parsed : null;
}

const TimestampSummaryIndicator = ({
  globalTimeRange,
  timestampSummary,
}: TimestampSummaryIndicatorProps) => {
  const { t } = useTranslation('energyxplorer');

  if (
    !globalTimeRange ||
    typeof globalTimeRange.start !== 'number' ||
    typeof globalTimeRange.end !== 'number'
  ) {
    return null;
  }

  const maxEarliest = toDateTime(timestampSummary?.maxEarliestDataTimestamp);
  const minLatest = toDateTime(timestampSummary?.minLatestDataTimestamp);

  if (!maxEarliest || !minLatest) {
    return null;
  }

  const currentStart = DateTime.fromMillis(globalTimeRange.start);
  const currentEnd = DateTime.fromMillis(globalTimeRange.end);

  if (!currentStart.isValid || !currentEnd.isValid) {
    return null;
  }

  const syncedStartFormatted = maxEarliest
    .setLocale('da')
    .plus({ days: 1 })
    .toFormat('d. MMMM yyyy');
  const syncedEndFormatted = minLatest
    .setLocale('da')
    .minus({ days: 1 })
    .toFormat('d. MMMM yyyy');

  const isOutOfBounds = currentStart < maxEarliest || currentEnd > minLatest;
  const isCompletelyOutside =
    currentEnd < maxEarliest || currentStart > minLatest;
  const isInverted = maxEarliest > minLatest;
  const isStartTooEarly = currentStart < maxEarliest;
  const isEndTooLate = currentEnd > minLatest;

  if (!isOutOfBounds && !isInverted) {
    return null;
  }

  return (
    <Alert
      variant="outlined"
      severity="info"
      sx={{ p: 2, m: 2, maxWidth: 500, bgcolor: 'background.paper' }}
    >
      <Typography variant="body2" gutterBottom>
        {t('timestamp_indicator.intro')}
        <br />
        {t('timestamp_indicator.sync_note')}
      </Typography>
      {isInverted ? (
        <Typography variant="body2" sx={{ mt: 1 }}>
          {t('timestamp_indicator.inverted_range', {
            start: syncedStartFormatted,
            end: syncedEndFormatted,
          })}
          <br />
          {t('timestamp_indicator.contact_support')}
        </Typography>
      ) : isCompletelyOutside ? (
        <Typography variant="body2" sx={{ mt: 1 }}>
          {t('timestamp_indicator.completely_outside')}
          <br />
          {t('timestamp_indicator.available_period_between')}{' '}
          <strong>{syncedStartFormatted}</strong> {t('timestamp_indicator.and')}{' '}
          <strong>{syncedEndFormatted}</strong>.
        </Typography>
      ) : (
        <>
          {isStartTooEarly && isEndTooLate && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {t('timestamp_indicator.both_outside')}
              <br />
              {t('timestamp_indicator.allowed_period_between')}{' '}
              <strong>{syncedStartFormatted}</strong>{' '}
              {t('timestamp_indicator.and')}{' '}
              <strong>{syncedEndFormatted}</strong>.
            </Typography>
          )}
          {isStartTooEarly && !isEndTooLate && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {t('timestamp_indicator.start_too_early')}
              <br />
              {t('timestamp_indicator.earliest_allowed_start')}{' '}
              <strong>{syncedStartFormatted}</strong>.
            </Typography>
          )}
          {!isStartTooEarly && isEndTooLate && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {t('timestamp_indicator.end_too_late')}{' '}
              {t('timestamp_indicator.latest_allowed_end')}{' '}
              <strong>{syncedEndFormatted}</strong>.
            </Typography>
          )}
        </>
      )}
    </Alert>
  );
};

export default TimestampSummaryIndicator;
